// The slide-out's account of a stopped invoice: the problem as a headline, one Invoice – PO comparison, and the ways
// out. A difference from the PO asks which side is right; everything else leads with its one fix.
import type { ReactNode } from "react";
import type { Freight, InvoiceDetail } from "../api/types";
import { checkForCode, failedWording, plainSummary } from "./checks";
import { overline } from "./components";
import { figuresOf } from "./invoiceParts";
import { LadderButton, PRIMARY, billToOf, fixKindOf, type LadderAction } from "./ladder";
import { money, poRef } from "./format";
import { Icon } from "../ui";
import type { InvoiceActions } from "../screens/ProblemDetail";

export interface CompareRow { label: string; inv: string; po: string; diff: string | null; muted?: boolean }

export interface Problem {
  icon: string;
  /** review: something about the invoice; settings: a missing setting; tech: AutoVouch couldn't finish. */
  tone: "review" | "settings" | "tech";
  title: string;
  why: string | null;
  rows: CompareRow[];
  /** Line differences and anything else without figures, one sentence each. */
  items: string[];
}

export interface Answer { who: string; acts: LadderAction[]; note?: string }

export type Plan =
  | { ask: string; answers: Answer[]; tail: { text: string; act: LadderAction } | null }
  | { ask: null; main: LadderAction; note?: string; more: LadderAction[]; tail: { text: string; act: LadderAction } | null };

const DIFFERENCE_CHECKS = ["Freight", "Tax", "Total", "Line items"];

/** The freight the matcher allows for a PO value, the same sum as ToleranceOptions.AllowedDifference. */
export function allowedDifference(t: Freight, po: number): number {
  const fromPercent = t.percent != null ? Math.round(Math.abs(po) * t.percent) / 100 : null;
  let allowed = fromPercent != null && t.amount != null ? (t.combine === "Greater" ? Math.max(fromPercent, t.amount) : Math.min(fromPercent, t.amount)) : fromPercent ?? t.amount ?? 0;
  if (t.max != null) allowed = Math.min(allowed, t.max);
  return Math.max(allowed, 0);
}

const num = (s: string) => Number(s.replace(/[,−]/g, (c) => (c === "−" ? "-" : "")));
const more = (n: number, cur: string) => `${money(Math.abs(n), cur)} ${n < 0 ? "less" : "more"} than the PO`;

/** The differences from the PO the matcher recorded: figures for freight, tax and the total; a sentence for anything else. */
function differences(detail: InvoiceDetail, cur: string): { rows: CompareRow[]; items: { code: string; text: string }[]; figures: { label: string; inv: number; po: number; delta: number }[] } {
  const rows: CompareRow[] = [];
  const items: { code: string; text: string }[] = [];
  const figures: { label: string; inv: number; po: number; delta: number }[] = [];
  for (const d of detail.discrepancies ?? []) {
    if (!DIFFERENCE_CHECKS.includes(checkForCode(d.code) ?? "")) continue;
    const f = d.code === "FREIGHT" || d.code === "TAX" || d.code === "TOTAL" ? figuresOf(d.message) : null;
    if (f) {
      const inv = num(f.inv), po = num(f.po), delta = d.delta ?? inv - po;
      const name = f.pre || checkForCode(d.code)!;
      figures.push({ label: name, inv, po, delta });
      rows.push({ label: name, inv: money(inv, cur), po: money(po, cur), diff: (delta < 0 ? "−" : "+") + money(Math.abs(delta), cur) });
    } else items.push({ code: d.code, text: d.message });
  }
  // Line differences add up to a lines figure; shown when the lines totals differ.
  const il = detail.invoiceTotals?.lines, pl = detail.poTotals?.lines;
  if (items.some((i) => i.code.startsWith("LINE_")) && il != null && pl != null && Math.abs(il - pl) > 0.001) rows.unshift({ label: "Lines", inv: money(il, cur), po: money(pl, cur), diff: (il - pl < 0 ? "−" : "+") + money(Math.abs(il - pl), cur) });
  // The total, for context, when it isn't one of the differences.
  const it = detail.invoiceTotals?.total, pt = detail.poTotals?.total;
  if (rows.length && !figures.some((f) => f.label.startsWith("Total")) && it != null && pt != null) rows.push({ label: "Total", inv: money(it, cur), po: money(pt, cur), diff: Math.abs(it - pt) > 0.001 ? (it - pt < 0 ? "−" : "+") + money(Math.abs(it - pt), cur) : null, muted: true });
  return { rows, items, figures };
}

const LINE_TITLES: Record<string, string> = {
  LINE_MISMATCH: "A line differs from the PO",
  LINE_NOT_ON_PO: "An invoice line isn’t on the PO",
  LINE_NOT_INVOICED: "A PO line isn’t on the invoice",
};

export function problemOf(detail: InvoiceDetail, cur: string, freightTolerance: Freight | null): Problem {
  const inv = detail.invoice;
  const check = checkForCode(inv.code);
  const kind = fixKindOf(inv);
  const po = poRef(inv);
  const p = (icon: string, title: string, why: string | null, rows: CompareRow[] = [], tone: Problem["tone"] = "review"): Problem => ({ icon, tone, title, why, rows, items: [] });

  if (DIFFERENCE_CHECKS.includes(check ?? "")) {
    const { rows, items, figures } = differences(detail, cur);
    const count = figures.length + items.length;
    if (count > 1) {
      const lines = figures.length === 0 && items.every((i) => i.code.startsWith("LINE_"));
      return { icon: "alert-triangle", tone: "review", title: lines ? `${count} lines differ from the PO` : `${count} things differ from the PO`, why: null, rows, items: items.map((i) => i.text) };
    }
    const f = figures[0];
    if (f) {
      let why: string | null = null;
      if (f.label === "Freight" && freightTolerance) {
        const allowed = allowedDifference(freightTolerance, f.po);
        const over = Math.abs(f.delta) - allowed;
        why = f.po === 0 ? "The PO has no freight" : allowed === 0 ? "Your freight tolerance is an exact match" : over > 0 ? `${money(over, cur)} over your ${money(allowed, cur)} tolerance` : null;
      } else if (f.label === "Tax") why = "Tax must match the PO";
      return { icon: "alert-triangle", tone: "review", title: `${f.label} is ${more(f.delta, cur)}`, why, rows, items: [] };
    }
    const one = items[0];
    return { icon: "alert-triangle", tone: "review", title: (one && LINE_TITLES[one.code]) ?? `${failedWording(check!)} from the PO`, why: one?.text ?? inv.summary, rows, items: [] };
  }

  switch (kind) {
    case "api": return p("plug-zap", /rejected|401|403/i.test(inv.summary ?? inv.code ?? "") ? "Syncore rejected the API key" : "Syncore isn’t connected", "The invoice is fine. It’s checked again once the API key is saved.", [], "settings");
    case "signin": return p("plug-zap", inv.code === "SYNCORE_LOGIN_FAILED" ? "Syncore rejected the sign-in for attaching" : "The Syncore sign-in for attaching isn’t set up", "The invoice is fine. The PDF can’t be attached to the job until the sign-in works.", [], "settings");
    case "approve": {
      const limit = /over your ([\d,.]+) limit/i.exec(inv.summary ?? "")?.[1];
      return p("wallet", limit ? `Over your ${money(num(limit), cur)} limit` : "Over your limit", "Every check passed. Invoices above the limit wait for a person.");
    }
    case "setpo": {
      const printed = detail.printedPo && detail.printedPo !== po ? `Printed on the invoice: ${detail.printedPo}` : null;
      if (inv.code === "PO_NOT_FOUND") return p("hash", po ?? detail.printedPo ? `PO ${po ?? detail.printedPo} isn’t in Syncore` : "The PO isn’t in Syncore", po ? printed : null);
      if (inv.code === "MULTIPLE_PO_REFERENCES") return p("hash", "The invoice names more than one PO", printed);
      if (inv.code === "PO_REFERENCE_FORMAT") return p("hash", "The PO number doesn’t read as a job and PO", printed);
      return p("hash", "No PO number on the invoice", printed);
    }
    case "supplier": return p("alert-triangle", inv.code === "SUPPLIER_UNKNOWN" ? "The supplier’s name couldn’t be read" : "Supplier doesn’t match the PO", null, [{ label: "Supplier", inv: detail.supplierInvoiceName ?? inv.supplier ?? "—", po: detail.po?.supplier ?? "—", diff: null }]);
    case "billto": {
      const m = /billed to (.+?), not (.+)$/i.exec(inv.summary ?? "");
      return p("building-2", m ? `Billed to ${m[1]}, not ${m[2]}` : "Not billed to you", "That name isn’t one of your Billed to names.");
    }
    case "duplicate": return p("copy", inv.code === "ALREADY_VOUCHED" ? `Invoice ${inv.invoiceNumber ?? ""} is already on PO ${po ?? ""}` : `Invoice ${inv.invoiceNumber ?? ""} was already approved`, "Nothing changed in Syncore.");
    default: break;
  }

  if (check === "PO is open") {
    const status = /is (.+?), not Open/i.exec(inv.summary ?? "")?.[1];
    return p("alert-triangle", `PO ${po ?? ""} isn’t open`, status ? `It’s ${status} in Syncore.` : null);
  }
  if (check === "PO adds up") return p("alert-triangle", "The PO’s lines don’t add up", (inv.summary ?? "").replace(/^Couldn.t compare lines: /, "") || null);
  if (check === "Invoice adds up") {
    const t = detail.invoiceTotals;
    const sum = t && t.lines != null && t.freight != null && t.tax != null ? t.lines + t.freight + t.tax : null;
    return p("file-search", "The invoice doesn’t add up", sum != null && t?.total != null ? `Lines, freight and tax come to ${money(sum, cur)}, not the ${money(t.total, cur)} total.` : null);
  }
  if (check === "Is an invoice") return p("file-search", "This doesn’t look like an invoice", inv.summary);
  if (check === "Invoice #, date & total found") return p("file-search", inv.summary ?? failedWording(check), null);
  if (check === "Couldn’t process") return p("cloud-off", plainSummary(inv), "Nothing changed in Syncore.", [], "tech");
  return p("alert-triangle", check ? failedWording(check) : inv.codeTitle ?? "Needs a look", inv.summary);
}

/** The ways out of a problem, as buttons. `navigate` opens settings; the rest go through the shared actions. */
export function planOf(detail: InvoiceDetail, actions: InvoiceActions, opts: { navigate: (to: string) => void }): Plan {
  const inv = detail.invoice;
  const kind = fixKindOf(inv);
  const check = checkForCode(inv.code);
  const url = detail.po?.syncoreUrl;
  const again: LadderAction = { label: "Check again", icon: "refresh-cw", onClick: () => actions.checkAgain([inv.id]) };
  const openPo: LadderAction | null = url ? { label: "Open PO in Syncore", icon: "external-link", onClick: () => window.open(url, "_blank", "noopener") } : null;
  const upload: LadderAction = { label: "Upload a corrected invoice…", icon: "upload", onClick: () => actions.replace(inv) };
  const reject: LadderAction = { label: "Reject invoice…", icon: "x", onClick: () => actions.resolve(inv) };
  const yourself = openPo ? { text: "Changed it in Syncore yourself?", act: again } : null;

  if (DIFFERENCE_CHECKS.includes(check ?? "")) {
    const invoiceRight: Answer = kind === "asinvoiced"
      ? { who: "The invoice", acts: [{ label: PRIMARY.asinvoiced[0], icon: PRIMARY.asinvoiced[1], onClick: () => actions.approveAsInvoiced(inv) }], note: asInvoicedNote(detail, actions.currency) }
      : openPo ? { who: "The invoice", acts: [openPo], note: "Change the PO to match, then check again" } : { who: "The invoice", acts: [again], note: "Change the PO in Syncore to match first" };
    return { ask: "Which is right?", answers: [invoiceRight, { who: "The PO", acts: [upload, reject] }], tail: invoiceRight.acts[0] === again ? null : yourself };
  }

  switch (kind) {
    case "api": return { ask: null, main: { label: "Open Syncore settings", icon: "settings-2", onClick: () => opts.navigate("/settings/syncore") }, more: [], tail: null };
    case "signin": return { ask: null, main: { label: "Open Syncore settings", icon: "settings-2", onClick: () => opts.navigate("/settings/syncore") }, more: [], tail: { text: "Approve it without attaching the PDF?", act: again } };
    case "approve": return { ask: null, main: { label: "Approve now", icon: "check", onClick: () => actions.approveOverLimit(inv) }, note: "Approves the PO in Syncore", more: [reject], tail: null };
    case "setpo": return { ask: null, main: { label: "Set PO number…", icon: "hash", onClick: () => actions.setPo(inv) }, note: "Then it’s checked against that PO", more: [upload, reject], tail: null };
    case "supplier": return {
      ask: "Which is right?",
      answers: [
        { who: "The invoice", acts: [{ label: "This supplier is OK", icon: "badge-check", onClick: () => actions.supplierOk(inv.id) }], note: "Remembers the name, then checks again" },
        { who: "The PO", acts: [upload, reject] },
      ],
      tail: openPo ? { text: "Changed the supplier in Syncore yourself?", act: again } : null,
    };
    case "billto": return {
      ask: "Is this you?",
      answers: [
        { who: "Yes", acts: [{ label: "Add to Billed to", icon: "building-2", onClick: () => opts.navigate(`/settings/matching?billTo=${encodeURIComponent(billToOf(inv) ?? detail.billTo ?? "")}`) }], note: "Save it under Settings → Invoice checks, then check again" },
        { who: "No", acts: [reject] },
      ],
      tail: { text: "Saved the name?", act: again },
    };
    case "duplicate": return { ask: null, main: { label: PRIMARY.duplicate[0], icon: "x", onClick: () => actions.rejectNow(inv, "Duplicate") }, note: "Records the reason Duplicate", more: [], tail: { text: "A different invoice with the same number?", act: { ...reject, label: "Reject with your own reason…" } } };
    default: break;
  }

  if (check === "PO is open" || check === "PO adds up") {
    const note = check === "PO is open" ? "Reopen it in Syncore, then check again" : "Fix the PO in Syncore, then check again";
    return openPo ? { ask: null, main: openPo, note, more: [reject], tail: yourself } : { ask: null, main: again, more: [reject], tail: null };
  }
  if (check === "Couldn’t process") return { ask: null, main: again, more: [], tail: { text: "Still stuck?", act: reject } };
  if (check === "Is an invoice" || check === "Invoice adds up" || check === "Invoice #, date & total found") return { ask: null, main: again, note: "Reads the PDF again", more: [upload, reject], tail: null };
  return openPo ? { ask: null, main: openPo, note: "Fix it in Syncore, then check again", more: [again, reject], tail: null } : { ask: null, main: again, more: [upload, reject], tail: null };
}

/** What "Approve as invoiced" changes on the PO in Syncore: the freight it sets, the Invoice adjustment line it adds. */
export function asInvoicedChanges(detail: InvoiceDetail, cur: string): { field: string; from: string | null; to: string }[] {
  const changes: { field: string; from: string | null; to: string }[] = [];
  const codes = (detail.discrepancies ?? []).map((d) => d.code);
  const pf = detail.poTotals?.freight, inf = detail.invoiceTotals?.freight;
  if (codes.includes("FREIGHT") && inf != null) changes.push({ field: "Freight", from: pf != null ? money(pf, cur) : null, to: money(inf, cur) });
  const lines = detail.invoice.linesDifference ?? (codes.some((c) => c.startsWith("LINE_")) ? (detail.discrepancies ?? []).filter((d) => d.code.startsWith("LINE_")).reduce((s, d) => s + (d.delta ?? 0), 0) : null);
  if (lines != null && Math.abs(lines) > 0.001) changes.push({ field: "Invoice adjustment line", from: null, to: (lines < 0 ? "−" : "+") + money(Math.abs(lines), cur) });
  return changes;
}

/** "Sets PO freight to $64.68, adds a +$12.30 Invoice adjustment line, then approves". */
function asInvoicedNote(detail: InvoiceDetail, cur: string): string {
  const parts = asInvoicedChanges(detail, cur).map((c) => (c.field === "Freight" ? `Sets PO freight to ${c.to}` : `adds a ${c.to} Invoice adjustment line`));
  if (!parts.length) return "Changes the PO in Syncore to match, then approves";
  parts[0] = parts[0][0].toUpperCase() + parts[0].slice(1);
  return `${parts.join(", ")}, then approves`;
}

// ---------------------------------------------------------------- the pieces

const label = { font: "700 10.5px/1 var(--font-body)", letterSpacing: ".07em", textTransform: "uppercase" as const, color: "var(--text-muted)" };

export function ProblemHead({ problem, children }: { problem: Problem; children?: ReactNode }) {
  const color = problem.tone === "settings" ? "var(--text-brand)" : problem.tone === "tech" ? "var(--text-muted)" : "var(--status-review-fg)";
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      <Icon name={problem.icon} size={22} style={{ color, flex: "none", marginTop: 2 }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
        <h3 style={{ margin: 0, font: "800 19px/1.3 var(--font-display)", color: "var(--text-strong)" }}>{problem.title}</h3>
        {problem.why && <span style={{ font: "500 14px/1.45 var(--font-body)", color: "var(--text-muted)" }}>{problem.why}</span>}
        {children}
      </div>
    </div>
  );
}

export function Compare({ rows, items }: { rows: CompareRow[]; items: string[] }) {
  if (!rows.length && !items.length) return null;
  const diffs = rows.some((r) => r.diff !== null);
  const cols = diffs ? "minmax(64px,92px) repeat(3,minmax(max-content,1fr))" : "92px repeat(2,minmax(0,1fr))";
  const cell = { font: "600 14px/1.35 var(--font-body)", fontVariantNumeric: "tabular-nums" as const, ...(diffs ? { whiteSpace: "nowrap" as const } : { overflowWrap: "anywhere" as const }) };
  return (
    <div style={{ borderRadius: "var(--radius-lg)", boxShadow: "inset 0 0 0 1px var(--border-subtle)", background: "var(--surface-card)", overflowX: "auto" }}>
      {rows.length > 0 && (
        <div role="table" style={{ display: "flex", flexDirection: "column", gap: 10, padding: "12px 16px" }}>
          <div role="row" style={{ display: "grid", gridTemplateColumns: cols, gap: 12 }}><span role="columnheader" /><span role="columnheader" style={label}>Invoice</span><span role="columnheader" style={label}>PO</span>{diffs && <span role="columnheader" style={label}>Difference</span>}</div>
          {rows.map((r) => (
            <div role="row" key={r.label} style={{ display: "grid", gridTemplateColumns: cols, gap: 12, alignItems: "baseline", color: r.muted ? "var(--text-muted)" : "var(--text-strong)" }}>
              <span role="rowheader" style={{ ...cell, fontWeight: 700 }}>{r.label}</span>
              <span role="cell" style={cell}>{r.inv}</span>
              <span role="cell" style={cell}>{r.po}</span>
              {diffs && <span role="cell" style={{ ...cell, color: r.muted ? undefined : "var(--status-mismatch-fg)" }}>{r.diff ?? "—"}</span>}
            </div>
          ))}
        </div>
      )}
      {items.length > 0 && (
        <ol style={{ margin: 0, padding: "10px 16px 10px 34px", borderTop: rows.length ? "1px solid var(--border-subtle)" : "none", display: "flex", flexDirection: "column", gap: 6, font: "500 14px/1.45 var(--font-body)", color: "var(--text-body)" }}>
          {items.map((t, i) => <li key={i}>{t}</li>)}
        </ol>
      )}
    </div>
  );
}

function Tail({ tail, viewer }: { tail: { text: string; act: LadderAction }; viewer: boolean }) {
  return (
    <span style={{ font: "500 14px var(--font-body)", color: "var(--text-muted)" }}>
      {tail.text}{" "}
      <button type="button" disabled={viewer} onClick={tail.act.onClick} style={{ border: 0, padding: 0, background: "none", font: "700 14px var(--font-body)", color: viewer ? "var(--text-subtle)" : "var(--text-link)", cursor: viewer ? "default" : "pointer" }}>{tail.act.label}</button>
    </span>
  );
}

export function PlanView({ plan, viewer }: { plan: Plan; viewer: boolean }) {
  const note = (t?: string) => t && <span style={{ font: "500 13px/1.4 var(--font-body)", color: "var(--text-muted)" }}>{t}</span>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {plan.ask !== null ? (
        <>
          <span style={{ ...overline, color: "var(--text-brand)" }}>{plan.ask}</span>
          <div style={{ borderRadius: "var(--radius-lg)", boxShadow: "inset 0 0 0 1px var(--border-subtle)", background: "var(--surface-card)" }}>
            {plan.answers.map((a, i) => (
              <div key={a.who} style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", alignItems: "flex-start", padding: "14px 16px", borderTop: i ? "1px solid var(--border-subtle)" : "none" }}>
                <span style={{ flex: "0 0 100px", font: "700 14px/32px var(--font-body)", color: "var(--text-strong)" }}>{a.who}</span>
                <div style={{ flex: "1 1 220px", display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{a.acts.map((x, j) => <LadderButton key={x.label} a={{ ...x, primary: i === 0 && j === 0 }} viewer={viewer} />)}</div>
                  {note(a.note)}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
            <LadderButton a={{ ...plan.main, primary: true }} size="md" viewer={viewer} />
            {note(plan.note)}
          </div>
          {plan.more.length > 0 && <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{plan.more.map((x) => <LadderButton key={x.label} a={x} viewer={viewer} />)}</div>}
        </>
      )}
      {plan.tail && <Tail tail={plan.tail} viewer={viewer} />}
      {viewer && <span style={{ font: "600 13px var(--font-body)", color: "var(--text-muted)" }}>View only – an Approver or Admin can fix this</span>}
    </div>
  );
}

